import { usePaymentStore } from "@/lib/store/paymentStore";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { useNavigate } from "react-router";
import Webcam from "react-webcam";
import { useUploadPhoto } from "./api/useUploadPhoto";
import { toast } from "sonner";
import { useFrameStore } from "@/lib/store/frameStore";
import { usePhotoHistoryStore } from "@/lib/store/photoHistoryStore";
import { ApiError } from "@/api";
import { track, trackError } from "@/lib/analytics/track";
import { cropToAspectRatio } from "@/lib/utils/cameraUtils";
import {
  findCountdownAnomaly,
  type CountdownTick,
} from "@/lib/utils/countdownTiming";
import { AspectRatio } from "@/lib/model/Frame";
import axios from "axios";
import * as Sentry from "@sentry/react";

type CapturePhase =
  | "IDLE"              // Initial state, ready to start
  | "COUNTDOWN"         // Counting down before photo (5...4...3...)
  | "CAPTURING"         // Flash + taking photo
  | "PHOTO_COMPLETE"    // One photo done, maybe more to go
  | "ALL_CAPTURED"      // All photos captured, ready to upload
  | "UPLOADING"         // Uploading photos to server
  | "COMPLETE"          // Upload succeeded, about to navigate
  | "ERROR";            // Upload or capture failed

export const DEFAULT_COUNT_DOWN_TIME = 5;
const FLASH_DURATION = 150;
const DELAY_BETWEEN_PHOTOS = 1000;

const isTimeoutError = (error: unknown) =>
  axios.isAxiosError(error) &&
  (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT");

// Helper function to convert base64 data URL to File
function dataUrlToFile(dataUrl: string, filename: string): File {
  const byteCharacters = atob(dataUrl.split(",")[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "image/jpeg" });

  return new File([blob], filename, { type: "image/jpeg" });
}

export interface PhotoCaptureState {
  // Phase management
  phase: CapturePhase;

  // Countdown state
  countdown: number;                    // 5, 4, 3, 2, 1, 0

  // Photo tracking
  captureCount: number;                 // Photos successfully captured
  captureAttempts: number;              // Total attempts (including retries)
  capturedImages: string[];             // Base64 data URLs of successful photos
  currentPhotoIndex: number;            // Which photo we're working on (0-based)

  // Retry logic
  retryCount: number;                   // Retries for current photo
  maxRetries: number;                   // 3 retries per photo (constant)

  // UI state
  isFlashing: boolean;                  // Flash animation active

  // Error handling
  error: string | null;                 // User-facing error message

  // Upload result
  jobId?: string;                       // Set on UPLOAD_SUCCESS
}

type PhotoCaptureAction =
  | { type: "START_CAPTURE" }
  | { type: "TICK_COUNTDOWN" }
  | { type: "COUNTDOWN_COMPLETE" }
  | { type: "START_FLASH" }
  | { type: "END_FLASH" }
  | { type: "PHOTO_CAPTURED"; payload: string | null }  // null = capture failed
  | { type: "NEXT_PHOTO_DELAY_COMPLETE" }
  | { type: "START_UPLOAD" }
  | { type: "UPLOAD_SUCCESS"; payload: { jobId: string } }
  | { type: "UPLOAD_ERROR"; payload: { error: unknown } }
  | { type: "RESET" };

const initialState: PhotoCaptureState = {
  phase: "IDLE",
  countdown: DEFAULT_COUNT_DOWN_TIME,
  captureCount: 0,
  captureAttempts: 0,
  capturedImages: [],
  currentPhotoIndex: 0,
  retryCount: 0,
  maxRetries: 3,
  isFlashing: false,
  error: null,
};

export function usePhotoCapture() {
  const navigate = useNavigate();
  const webCamRef = useRef<Webcam>(null);
  const { frame } = useFrameStore();
  const imageCount = frame?.imagesCount || 0;
  const aspectRatio = frame?.aspectRatio || AspectRatio.ONE_TO_ONE;
  const { mutate: uploadPhoto } = useUploadPhoto();
  const { paymentPayload, resetPaymentPayload } = usePaymentStore();
  const addEntry = usePhotoHistoryStore((state) => state.addEntry);

  // Reducer defined inside hook to access imageCount via closure
  function photoCaptureReducer(
    state: PhotoCaptureState,
    action: PhotoCaptureAction
  ): PhotoCaptureState {
    switch (action.type) {
      case "START_CAPTURE":
        return {
          ...initialState,
          phase: "COUNTDOWN",
          countdown: DEFAULT_COUNT_DOWN_TIME,
        };

      case "TICK_COUNTDOWN":
        return {
          ...state,
          countdown: state.countdown - 1,
        };

      case "COUNTDOWN_COMPLETE":
        return {
          ...state,
          phase: "CAPTURING",
        };

      case "START_FLASH":
        return {
          ...state,
          isFlashing: true,
        };

      case "END_FLASH":
        return {
          ...state,
          isFlashing: false,
        };

      case "PHOTO_CAPTURED": {
        const photo = action.payload;
        const totalNeeded = imageCount; // Use imageCount from hook scope

        if (photo) {
          // Success! Add photo and check if done
          const newImages = [...state.capturedImages, photo];
          const newIndex = state.currentPhotoIndex + 1;
          const newAttempts = state.captureAttempts + 1;

          if (newIndex >= totalNeeded) {
            // All photos captured successfully
            return {
              ...state,
              capturedImages: newImages,
              captureCount: newImages.length,
              currentPhotoIndex: newIndex,
              captureAttempts: newAttempts,
              retryCount: 0,
              phase: "ALL_CAPTURED",
            };
          }

          // More photos needed
          return {
            ...state,
            capturedImages: newImages,
            captureCount: newImages.length,
            currentPhotoIndex: newIndex,
            captureAttempts: newAttempts,
            retryCount: 0,
            phase: "PHOTO_COMPLETE",
          };
        } else {
          // Failed to capture photo
          const newAttempts = state.captureAttempts + 1;

          if (state.retryCount < state.maxRetries) {
            // Retry this photo
            return {
              ...state,
              retryCount: state.retryCount + 1,
              captureAttempts: newAttempts,
              phase: "COUNTDOWN",
              countdown: DEFAULT_COUNT_DOWN_TIME,
            };
          } else {
            // Max retries exceeded, fail the whole capture
            return {
              ...state,
              captureAttempts: newAttempts,
              phase: "ERROR",
              error: "Failed to capture photo after multiple attempts. Please try again.",
            };
          }
        }
      }

      case "NEXT_PHOTO_DELAY_COMPLETE":
        return {
          ...state,
          phase: "COUNTDOWN",
          countdown: DEFAULT_COUNT_DOWN_TIME,
        };

      case "START_UPLOAD":
        return {
          ...state,
          phase: "UPLOADING",
        };

      case "UPLOAD_SUCCESS":
        return {
          ...state,
          phase: "COMPLETE",
          jobId: action.payload.jobId,
        };

      case "UPLOAD_ERROR": {
        const error = action.payload.error;
        let errorMessage = "Failed to upload photos. Please try again.";

        // Handle specific error types
        if (isTimeoutError(error)) {
          errorMessage = "Uploading your photos timed out. Please check your connection and try again.";
          trackError({ type: "upload", message: errorMessage, source: "manual" });
        } else if (error instanceof ApiError) {
          errorMessage = error.body?.error?.userMessage
                      || error.body?.error?.message
                      || errorMessage;

          trackError({ type: "upload", message: errorMessage, source: "manual" });
        }

        // Show toast
        toast.error(errorMessage);
        track("upload_photos_error", { message: errorMessage });

        return {
          ...state,
          phase: "ERROR",
          error: errorMessage,
        };
      }

      case "RESET":
        return initialState;

      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(photoCaptureReducer, initialState);

  const takePhoto = useCallback(async (): Promise<string | null> => {
    if (!webCamRef.current) return null;

    const imageSrc = webCamRef.current.getScreenshot();
    if (!imageSrc) return null;

    try {
      const croppedImage = await cropToAspectRatio(imageSrc, aspectRatio);
      return croppedImage;
    } catch (error) {
      console.error("Failed to crop image:", error);
      return imageSrc;
    }
  }, [aspectRatio]);

  const countdownTicksRef = useRef<CountdownTick[]>([]);

  // Countdown effect - ticks down every second
  useEffect(() => {
    if (state.phase !== "COUNTDOWN") return;

    const ticks = countdownTicksRef.current;
    if (state.countdown === DEFAULT_COUNT_DOWN_TIME) ticks.length = 0;
    ticks.push({ countdown: state.countdown, at: performance.now() });

    if (state.countdown === 0) {
      const anomaly = findCountdownAnomaly(ticks);
      if (anomaly) {
        Sentry.captureMessage("Countdown timing anomaly", {
          level: "warning",
          extra: {
            ...anomaly,
            photo_index: state.currentPhotoIndex,
            retry_count: state.retryCount,
            visibility: document.visibilityState,
            hardware_concurrency: navigator.hardwareConcurrency,
          },
        });
      }

      // Countdown finished, trigger capture
      dispatch({ type: "COUNTDOWN_COMPLETE" });
      return;
    }

    // Tick down every second
    const timer = setTimeout(() => {
      dispatch({ type: "TICK_COUNTDOWN" });
    }, 1000);

    return () => clearTimeout(timer);
  }, [state.phase, state.countdown, state.currentPhotoIndex, state.retryCount]);

  // Flash + capture effect - runs when phase is CAPTURING
  useEffect(() => {
    if (state.phase !== "CAPTURING") return;

    // Start flash immediately
    dispatch({ type: "START_FLASH" });

    // End flash after 150ms, then take photo
    const flashTimer = setTimeout(() => {
      dispatch({ type: "END_FLASH" });

      // Capture photo after flash ends
      setTimeout(async () => {
        const photo = await takePhoto();
        dispatch({ type: "PHOTO_CAPTURED", payload: photo });
      }, 50);
    }, FLASH_DURATION);

    return () => clearTimeout(flashTimer);
  }, [state.phase, takePhoto]);

  // Photo delay effect - waits between photos
  useEffect(() => {
    if (state.phase !== "PHOTO_COMPLETE") return;

    const timer = setTimeout(() => {
      dispatch({ type: "NEXT_PHOTO_DELAY_COMPLETE" });
    }, DELAY_BETWEEN_PHOTOS);

    return () => clearTimeout(timer);
  }, [state.phase]);

  // Upload trigger effect - transitions from ALL_CAPTURED to UPLOADING
  useEffect(() => {
    if (state.phase !== "ALL_CAPTURED") return;

    dispatch({ type: "START_UPLOAD" });
  }, [state.phase]);

  // Upload execution effect - performs the actual upload
  useEffect(() => {
    if (state.phase !== "UPLOADING") return;

    const photoFiles = state.capturedImages.map((dataUrl, index) =>
      dataUrlToFile(dataUrl, `photo-${index + 1}.jpg`)
    );

    uploadPhoto(
      {
        photos: photoFiles,
        childFrameId: paymentPayload?.childFrameId ?? "",
      },
      {
        onSuccess: (data) => {
          addEntry({
            jobId: data.jobId,
            createdAt: new Date().toISOString(),
            transactionId: paymentPayload?.transactionId,
            childFrameId: paymentPayload?.childFrameId,
            frameName: frame?.name,
          });
          dispatch({
            type: "UPLOAD_SUCCESS",
            payload: { jobId: data.jobId },
          });
        },
        onError: (error) => {
          dispatch({
            type: "UPLOAD_ERROR",
            payload: { error },
          });
        },
      }
    );
  }, [state.phase, state.capturedImages, paymentPayload, uploadPhoto, addEntry, frame]);

  // Navigation effect - redirects when upload completes
  useEffect(() => {
    if (state.phase !== "COMPLETE" || !state.jobId) return;

    navigate(`/result?jobId=${state.jobId}`);
    resetPaymentPayload();

    // Reset state after navigation for next use
    setTimeout(() => {
      dispatch({ type: "RESET" });
    }, 500);
  }, [state.phase, state.jobId, navigate, resetPaymentPayload]);

  // Error navigation effect - handles payment token invalid case
  useEffect(() => {
    if (state.phase !== "ERROR" || !state.error) return;

    // If payment token is invalid, navigate back to frame selection
    if (state.error.includes("payment") || state.error.includes("token")) {
      resetPaymentPayload();
      setTimeout(() => {
        navigate("/photo-booth");
      }, 2000);
    }
  }, [state.phase, state.error, navigate, resetPaymentPayload]);

  const handleCapture = useCallback(() => {
    dispatch({ type: "START_CAPTURE" });
  }, []);

  // Error tracking effect
  useEffect(() => {
    if (state.phase !== "ERROR" || !state.error) return;

    Sentry.captureMessage("Photo capture error", {
      level: "warning",
      extra: {
        error_message: state.error,
        phase: state.phase,
        captured_count: state.captureCount,
        expected_count: imageCount,
        retry_count: state.retryCount,
        total_attempts: state.captureAttempts,
      }
    });
  }, [state.phase, state.error, state.captureCount, state.retryCount, state.captureAttempts, imageCount]);

  // Success tracking effect
  useEffect(() => {
    if (state.phase !== "COMPLETE") return;

    track("photo_capture_complete", {
      photo_count: state.captureCount,
      total_attempts: state.captureAttempts,
      had_retries: state.captureAttempts > state.captureCount,
    });
  }, [state.phase, state.captureCount, state.captureAttempts]);

  // Derived properties for backward compatibility
  const isCapturing = state.phase !== "IDLE" && state.phase !== "ERROR" && state.phase !== "COMPLETE";
  const isUploadingPhoto = state.phase === "UPLOADING";

  return {
    webCamRef,
    state: {
      ...state,
      isCapturing,
      isUploadingPhoto,
      // Keep these for component compatibility
      countDownTime: state.countdown,
      capturedImages: state.capturedImages,
      captureCount: state.captureCount,
      isFlashing: state.isFlashing,
      // Deprecated but kept for compatibility
      uploadStatus: { current: isUploadingPhoto ? "uploading" : "idle" } as React.MutableRefObject<"idle" | "uploading">,
    },
    handleCapture,
  };
}
